import { CSSProperties, useId } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { calculateWheelSize, formatWheelSize, WheelSize } from "@/lib/wheelSize";

export default function AnimatedWheel({ size, scaleDiameter, speed, playing, color, label, view = "2d", showDimensions = true }: {
  size: WheelSize;
  scaleDiameter: number;
  speed: number;
  playing: boolean;
  color: string;
  label: string;
  view?: "2d" | "3d";
  showDimensions?: boolean;
}) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const reducedMotion = useReducedMotion();
  const specs = calculateWheelSize(size);
  const radius = specs.diameter / scaleDiameter * 100;
  const rimRadius = specs.rim / scaleDiameter * 100;
  const width = size.width / scaleDiameter * 200;
  const faceScale = view === "3d" ? 0.78 : 1;
  const depth = view === "3d" ? width * 0.65 : 0;
  const cx = 210 - depth / 2;
  const cy = 270 - radius;
  const rx = radius * faceScale;
  const transition = { duration: reducedMotion ? 0 : 0.45 };
  const bodyPath = `M ${cx} ${cy - radius} L ${cx + depth} ${cy - radius} A ${rx} ${radius} 0 0 1 ${cx + depth} ${cy + radius} L ${cx} ${cy + radius} A ${rx} ${radius} 0 0 0 ${cx} ${cy - radius} Z`;
  const markingRadius = (radius + rimRadius) / 2;
  const markingPath = `M ${-markingRadius} 0 A ${markingRadius} ${markingRadius} 0 0 1 ${markingRadius} 0`;
  // Slow real-world rotation by 20x so the relative rolling speeds remain visible.
  const duration = speed > 0 ? specs.circumference / (speed / 3.6 * 1000) * 20 : 1;
  const spinStyle: CSSProperties = {
    transformOrigin: "0px 0px",
    animationDuration: `${duration}s`,
    animationPlayState: playing && speed > 0 ? "running" : "paused",
  };

  return (
    <figure className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
      <figcaption className="text-center text-lg font-semibold" style={{ color }}>{label}: {formatWheelSize(size)}</figcaption>
      <svg viewBox="0 0 440 420" role="img" aria-label={`${label} ${view === "3d" ? "3D-style" : "2D"} tyre ${formatWheelSize(size)}: width ${size.width} mm, aspect ratio ${size.aspectRatio}%, sidewall ${specs.sidewall.toFixed(1)} mm, radial construction, rim ${size.rimDiameter} inches, overall diameter ${specs.diameter.toFixed(1)} mm`} className="mx-auto w-full max-w-lg">
        <defs>
          <radialGradient id={`rim-${id}`}>
            <stop offset="0" stopColor="#64748b" />
            <stop offset="0.7" stopColor="#334155" />
            <stop offset="1" stopColor="#94a3b8" />
          </radialGradient>
          <linearGradient id={`tread-${id}`} x1="0" x2="1">
            <stop offset="0" stopColor="#475569" />
            <stop offset="0.5" stopColor="#111827" />
            <stop offset="1" stopColor="#334155" />
          </linearGradient>
          <marker id={`arrow-${id}`} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 Z" fill={color} />
          </marker>
        </defs>
        <motion.path initial={false} animate={{ d: bodyPath }} transition={transition} fill={`url(#tread-${id})`} stroke="#0f172a" strokeWidth="2" />
        {view === "3d" && Array.from({ length: 13 }, (_, index) => {
          const angle = -Math.PI / 2 + index * Math.PI / 12;
          const x = cx + rx * Math.cos(angle);
          const y = cy + radius * Math.sin(angle);
          return <motion.line key={index} initial={false} animate={{ x1: x, y1: y, x2: x + depth, y2: y }} transition={transition} stroke="#64748b" strokeWidth="2" />;
        })}
        {/* Native SVG transforms keep the face aligned; CSS transforms use different origins. */}
        <g data-dimension="wheel-face" transform={`translate(${cx} ${cy}) scale(${faceScale} 1)`}>
          <motion.circle data-dimension="outer-radius" initial={false} animate={{ r: radius }} transition={transition} fill="#171e2b" stroke={color} strokeWidth="2" />
          <motion.circle initial={false} animate={{ r: radius - 4 }} transition={transition} fill="none" stroke="#475569" strokeWidth="2" strokeDasharray="2 5" />
          <g className="wheel-rotation" style={spinStyle}>
            <motion.circle data-dimension="rim-radius" initial={false} animate={{ r: rimRadius }} transition={transition} fill={`url(#rim-${id})`} stroke="#e2e8f0" strokeWidth="2" />
            {[0, 60, 120, 180, 240, 300].map((angle) => (
              <g key={angle} transform={`rotate(${angle})`}>
                <motion.path initial={false} animate={{ d: `M -5 0 L -9 ${-rimRadius + 5} L 9 ${-rimRadius + 5} L 5 0 Z` }} transition={transition} fill="#0f172a" stroke="#cbd5e1" strokeWidth="1" />
              </g>
            ))}
            <circle r="10" fill="#cbd5e1" stroke="#334155" strokeWidth="2" />
            <motion.circle initial={false} animate={{ cy: radius - 5 }} transition={transition} r="3" fill={color} />
            <defs>
              <motion.path id={`marking-${id}`} initial={false} animate={{ d: markingPath }} transition={transition} />
            </defs>
            <text fill="#e2e8f0" fontWeight="700" fontSize={Math.max(4, Math.min(12, (radius - rimRadius) * 0.55))} letterSpacing="1">
              <textPath href={`#marking-${id}`} startOffset="50%" textAnchor="middle">{formatWheelSize(size)}</textPath>
            </text>
          </g>
        </g>
        <line x1="40" x2="365" y1="271" y2="271" stroke="#94a3b8" />
        {showDimensions && (
          <g fill={color} stroke={color} strokeWidth="1.5">
            <motion.line initial={false} animate={{ x1: 375, x2: 375, y1: cy - radius, y2: cy + radius }} transition={transition} markerStart={`url(#arrow-${id})`} markerEnd={`url(#arrow-${id})`} />
            <text x="397" y={cy} transform={`rotate(-90 397 ${cy})`} textAnchor="middle" stroke="none" fontSize="13">Overall {specs.diameter.toFixed(1)} mm</text>
            <motion.line initial={false} animate={{ x1: cx - rimRadius * faceScale, x2: cx + rimRadius * faceScale, y1: cy, y2: cy }} transition={transition} markerStart={`url(#arrow-${id})`} markerEnd={`url(#arrow-${id})`} />
            <rect x={cx - 54} y={cy - 8} width="108" height="20" rx="4" fill="#0f172a" stroke="#94a3b8" strokeWidth="1" />
            <text x={cx} y={cy + 6} textAnchor="middle" fill="#ffffff" stroke="none" fontSize="12">{size.rimDiameter}&quot; / {specs.rim.toFixed(1)} mm</text>
            <motion.line initial={false} animate={{ x1: cx, x2: cx, y1: cy + rimRadius, y2: cy + radius }} transition={transition} markerStart={`url(#arrow-${id})`} markerEnd={`url(#arrow-${id})`} />
            <motion.path initial={false} animate={{ d: `M ${cx - 5} ${cy + (rimRadius + radius) / 2} L 75 293 L 40 293` }} transition={transition} fill="none" />
            <text x="40" y="311" stroke="none" fontSize="13">Sidewall {specs.sidewall.toFixed(1)} mm ({size.aspectRatio}%)</text>
          </g>
        )}
        <text x="220" y="337" textAnchor="middle" fill="#475569" fontSize="12">Front / tread view (width, same scale)</text>
        <motion.rect data-dimension="tyre-width" initial={false} animate={{ attrX: 220 - width / 2, width }} transition={transition} y="347" height="38" rx="7" fill={`url(#tread-${id})`} stroke="#334155" />
        {[-0.3, -0.1, 0.1, 0.3].map((position) => (
          <motion.line key={position} initial={false} animate={{ x1: 220 + width * position, x2: 220 + width * position }} transition={transition} y1="351" y2="381" stroke="#94a3b8" strokeWidth="2" />
        ))}
        {showDimensions && (
          <g stroke={color} fill={color}>
            <motion.line initial={false} animate={{ x1: 220 - width / 2, x2: 220 + width / 2 }} transition={transition} y1="396" y2="396" markerStart={`url(#arrow-${id})`} markerEnd={`url(#arrow-${id})`} />
            <text x="220" y="416" textAnchor="middle" stroke="none" fontSize="13">Width {size.width} mm</text>
          </g>
        )}
      </svg>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
        <div><dt className="font-semibold" style={{ color }}>Width ({size.width})</dt><dd className="mt-1 text-slate-600">{size.width} mm measured from sidewall to sidewall.</dd></div>
        <div><dt className="font-semibold" style={{ color }}>Aspect ratio ({size.aspectRatio})</dt><dd className="mt-1 text-slate-600">{size.aspectRatio}% of {size.width} mm = {specs.sidewall.toFixed(1)} mm sidewall height.</dd></div>
        <div><dt className="font-semibold" style={{ color }}>Construction (R)</dt><dd className="mt-1 text-slate-600">R means radial construction, used by most modern tyres.</dd></div>
        <div><dt className="font-semibold" style={{ color }}>Wheel diameter ({size.rimDiameter})</dt><dd className="mt-1 text-slate-600">{size.rimDiameter} inches ({specs.rim.toFixed(1)} mm), the diameter of the wheel the tyre fits.</dd></div>
      </dl>
    </figure>
  );
}
